import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';

const FAVORITES_ACCENT = '#EC4899';

const createStyles = (colors, isXS) => StyleSheet.create({
  wrap: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: isXS ? 28 : 40,
    paddingHorizontal: isXS ? 8 : 16,
  },
  iconRing: {
    width: isXS ? 72 : 80,
    height: isXS ? 72 : 80,
    borderRadius: isXS ? 22 : 24,
    backgroundColor: `${FAVORITES_ACCENT}18`,
    borderWidth: 1,
    borderColor: `${FAVORITES_ACCENT}40`,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  title: {
    textAlign: 'center',
    fontSize: isXS ? 22 : 26,
    lineHeight: isXS ? 28 : 32,
    marginBottom: 10,
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 440,
    lineHeight: 22,
    marginBottom: 24,
  },
  hints: {
    width: '100%',
    maxWidth: 480,
    gap: 10,
    marginBottom: 24,
  },
  hintCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: isXS ? 12 : 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
  },
  hintIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${FAVORITES_ACCENT}1A`,
  },
  hintCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  actions: {
    flexDirection: isXS ? 'column' : 'row',
    alignItems: 'center',
    gap: 10,
  },
  actionBtn: {
    minWidth: isXS ? 220 : 200,
  },
});

/**
 * Empty state when the tasks catalog "My favorites" scope has nothing to show.
 *
 * @param {'none' | 'category'} variant
 *   - `none` — user has no favorited templates or tasks at all
 *   - `category` — user has favorites, but none in the active category
 * @param {() => void} onBrowseTemplates — exits favorites / clears category filter
 */
function TasksFavoritesEmptyState({ variant = 'none', onBrowseTemplates }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const isCategory = variant === 'category';

  const title = isCategory
    ? (t('pages.tasks.favoritesEmptyCategoryTitle') || 'No favorites here')
    : (t('pages.tasks.favoritesEmptyTitle') || 'No favorites yet');

  const subtitle = isCategory
    ? (t('pages.tasks.favoritesEmptyCategorySubtitle')
      || 'You have saved favorites in other categories. Browse the full catalog or pick a different category.')
    : (t('pages.tasks.favoritesEmptySubtitle')
      || 'Pin templates and tasks you use often — tap the heart on any card and they will show up here.');

  const browseLabel = isCategory
    ? (t('pages.tasks.favoritesEmptyBrowseAll') || 'Show all favorites')
    : (t('pages.tasks.browseTemplates') || 'Browse templates');

  return (
    <View style={styles.wrap}>
      <View style={styles.iconRing}>
        <Icon name="heart" size={isXS ? 30 : 34} color={FAVORITES_ACCENT} solid />
      </View>

      <Text weight="extraBold" style={styles.title}>
        {title}
      </Text>
      <Text size="n" color="secondary" style={styles.subtitle}>
        {subtitle}
      </Text>

      {!isCategory ? (
        <View style={styles.hints}>
          <View style={styles.hintCard}>
            <View style={styles.hintIcon}>
              <Icon name="clipboard-list" size={16} color={FAVORITES_ACCENT} solid />
            </View>
            <View style={styles.hintCopy}>
              <Text size="s" weight="bold">
                {t('pages.tasks.favoritesEmptyTemplatesHint') || 'Favorite a template'}
              </Text>
              <Text size="s" color="secondary" style={{ lineHeight: 20 }}>
                {t('pages.tasks.favoritesEmptyTemplatesHintDesc')
                  || 'Browse the catalog and tap the heart on any template card.'}
              </Text>
            </View>
          </View>
          <View style={styles.hintCard}>
            <View style={styles.hintIcon}>
              <Icon name="calendar-clock" size={16} color={FAVORITES_ACCENT} solid />
            </View>
            <View style={styles.hintCopy}>
              <Text size="s" weight="bold">
                {t('pages.tasks.favoritesEmptyTasksHint') || 'Favorite a scheduled task'}
              </Text>
              <Text size="s" color="secondary" style={{ lineHeight: 20 }}>
                {t('pages.tasks.favoritesEmptyTasksHintDesc')
                  || 'Open one of your tasks and tap the heart to pin it here.'}
              </Text>
            </View>
          </View>
        </View>
      ) : null}

      <View style={styles.actions}>
        <Button
          text={browseLabel}
          variant="primary"
          prependIcon={isCategory ? 'heart' : 'grid-2'}
          onPress={onBrowseTemplates}
          style={styles.actionBtn}
        />
      </View>
    </View>
  );
}

export default React.memo(TasksFavoritesEmptyState);
